import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  QrCode, 
  Plus, 
  User, 
  Briefcase, 
  Mail, 
  Phone, 
  Share2, 
  Edit, 
  Trash2,
  Loader,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getFirestore, collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';

const DigitalIdentity = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch user's identities on component mount
  useEffect(() => {
    const fetchIdentities = async () => {
      if (!currentUser) {
        setIdentities([]);
        setLoading(false);
        return;
      }
      
      try {
        const db = getFirestore();
        
        // Query Firestore for identities
        const identitiesCollection = collection(db, "digitalIdentities");
        const identitiesQuery = query(identitiesCollection, where("userId", "==", currentUser.uid));
        const querySnapshot = await getDocs(identitiesQuery);
        
        const identitiesList = [];
        querySnapshot.forEach((doc) => {
          identitiesList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setIdentities(identitiesList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching identities:', error);
        setError('Failed to load your digital identities. Please try again later.');
        setLoading(false);
      }
    };

    fetchIdentities();
  }, [currentUser]);

  // Handle identity deletion
  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      // Delete from Firestore
      const db = getFirestore();
      await deleteDoc(doc(db, "digitalIdentities", deleteId));
      
      // Also try to delete from API if available
      try {
        await api.delete(`/digital-identity/${deleteId}`);
      } catch (apiError) {
        console.log('API delete failed, but Firestore delete succeeded:', apiError);
      }
      
      setIdentities(identities.filter(identity => identity.id !== deleteId));
      setShowDeleteModal(false);
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting identity:', error);
      setError('Failed to delete the digital identity. Please try again.');
    }
  };

  // Confirm deletion
  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-8 px-4">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-blue-100 rounded-full mb-4">
            <QrCode className="h-6 w-6 text-blue-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Digital Identity Hub</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Create and share professional digital business cards and personal landing pages with QR codes
          </p>
          
          <div className="mt-8">
            <Link
              to="/digital-identity/create"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-md shadow-md hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create New Digital Identity
            </Link>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md mb-6 flex items-start max-w-4xl mx-auto">
            <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Identities List */}
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="h-10 w-10 text-blue-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading your digital identities...</p>
            </div>
          ) : identities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {identities.map(identity => (
                <div 
                  key={identity.id} 
                  className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
                >
                  <div 
                    className="h-24 flex items-end p-4"
                    style={{ 
                      backgroundColor: identity.customization?.primaryColor || '#2563eb',
                      backgroundImage: identity.template === 'gradient' 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' 
                        : 'none'
                    }}
                  >
                    <h3 className="text-xl font-bold text-white">{identity.name}</h3>
                  </div>
                  
                  <div className="p-4">
                    {identity.title && (
                      <p className="text-gray-600 mb-4">{identity.title}</p>
                    )}
                    
                    <div className="space-y-2 mb-4">
                      {identity.contact.email && (
                        <div className="flex items-center text-sm">
                          <Mail className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="text-gray-700 truncate">{identity.contact.email}</span>
                        </div>
                      )}
                      
                      {identity.contact.phone && (
                        <div className="flex items-center text-sm">
                          <Phone className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="text-gray-700">{identity.contact.phone}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                      <Link
                        to={`/digital-identity/${identity.id}`}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View
                      </Link>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/digital-identity/${identity.id}/edit`)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 rounded-full hover:bg-blue-50"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => confirmDelete(identity.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-4">
                <QrCode className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Digital Identities Yet</h2>
              <p className="text-gray-600 mb-6">
                Create your first digital identity to share your contact information and social profiles with anyone via QR code.
              </p>
              <Link
                to="/digital-identity/create"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Your First Identity
              </Link>
            </div>
          )}
        </div>

        {/* Features Section */}
        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <QrCode className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">QR Code Sharing</h3>
              <p className="text-gray-600">
                Generate QR codes for your digital identity that can be scanned by anyone to instantly access your information.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Briefcase className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Professional Templates</h3>
              <p className="text-gray-600">
                Choose from multiple professionally designed templates to create a digital identity that matches your personal brand.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Share2 className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Sharing</h3>
              <p className="text-gray-600">
                Share your digital identity via QR code, link, or download as a vCard to add to contacts.
              </p>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-16 max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">1</div>
              <h3 className="font-medium text-gray-900 mb-2">Create Identity</h3>
              <p className="text-sm text-gray-600">Fill in your personal and contact information</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">2</div>
              <h3 className="font-medium text-gray-900 mb-2">Choose Template</h3>
              <p className="text-sm text-gray-600">Select a template and customize colors</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">3</div>
              <h3 className="font-medium text-gray-900 mb-2">Generate QR Code</h3>
              <p className="text-sm text-gray-600">Create a QR code for your digital identity</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold">4</div>
              <h3 className="font-medium text-gray-900 mb-2">Share</h3>
              <p className="text-sm text-gray-600">Share your QR code or link with others</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What is a Digital Identity?</h3>
              <p className="text-gray-600">
                A Digital Identity is a modern replacement for traditional business cards. It's a digital page that contains your contact information, social media profiles, and professional details that can be shared via QR code or link.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I share my Digital Identity?</h3>
              <p className="text-gray-600">
                You can share your Digital Identity by generating a QR code that others can scan with their smartphone camera, or by sharing a direct link via email, text message, or social media.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I create multiple Digital Identities?</h3>
              <p className="text-gray-600">
                Yes, you can create multiple Digital Identities for different purposes, such as one for professional networking and another for personal connections.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is my information secure?</h3>
              <p className="text-gray-600">
                You control what information you share on your Digital Identity. You can update or delete your Digital Identity at any time, and you can choose which contact details and social profiles to include.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this digital identity? This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalIdentity;
